import React from "react";
import { useNavigate } from "react-router-dom";

const EmprendedorSection = () => {
  const navigate = useNavigate();

  return (
    <section
      className="bg-black text-white py-16 px-6 md:px-12 lg:px-24 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] bg-repeat"
    >
      <div className="max-w-5xl mx-auto space-y-12">

        {/* Título principal */}
        <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 leading-tight">
          Ernesto Mora Soto:{" "}
          <span className="text-[#FFD700]">
            Pionero en la Revolución de la Seguridad y Salud en el Trabajo con Inteligencia Artificial
          </span>
        </h1>

        <p className="text-lg text-gray-300 font-semibold">
          CEO de ESGRYMA Consulting Group | Conferencista Internacional | Experto en{" "}
          <span className="text-[#FFD700] font-bold">Inteligencia Artificial aplicada a la Seguridad y Salud en el Trabajo (SST)</span>.
        </p>

        {/* Capítulo 1 */}
        <h2 className="text-2xl font-bold mt-10 text-[#FFD700]">Capítulo 1: El Latido que lo Cambió Todo</h2>
        <p className="text-gray-300 text-lg">
          <em>Cartagena, 1995.</em> El sonido del reloj a las 3:17 PM aún retumba en mi memoria. 
          Esa tarde, en la sala de mi casa, vi a mi hermano menor intentando desesperadamente un RCP a nuestra abuela. 
          Tenía apenas 13 años, pero ese acto de valentía improvisada me enseñó que el conocimiento puede marcar la diferencia entre la vida y la muerte. 
          No fue un profesor, ni un manual de seguridad… fue la crudeza de la vida la que me reveló que la preparación salva familias enteras.
        </p>
        <p className="text-gray-300 text-lg">
          A partir de ese día, comprendí que mi misión era mayor que conseguir un buen empleo. 
          Mi propósito era generar una transformación en cómo pensamos la seguridad laboral. 
          Hoy, esa experiencia infantil es la raíz de una carrera dedicada a la{" "}
          <span className="text-[#FFD700] font-bold">prevención de riesgos laborales en América Latina</span>. 
          Lo comparto constantemente en mi{" "}
          <a href="/blog" className="text-[#FFD700] font-bold hover:underline">blog sobre Seguridad y SST</a>, 
          donde detallo historias que muestran que la prevención no es teoría: es la diferencia entre volver a casa o no.
        </p>

        {/* Capítulo 2 */}
        <h2 className="text-2xl font-bold mt-10 text-[#FFD700]">Capítulo 2: La Caída que Forjó un Imperio de Prevención</h2>
        <p className="text-gray-300 text-lg">
          <em>2003.</em> Recuerdo vívidamente el grito que cortó el aire en una planta industrial en Cartagena. 
          Un trabajador cayó desde más de veinte metros. Vi su cuerpo chocar contra el suelo y, milagrosamente, sobrevivir. 
          Esa escena fue un punto de quiebre: decidí que no iba a dejar que la vida de las personas dependiera del azar. 
          Ese día nació lo que años después se consolidaría como{" "}
          <span className="text-[#FFD700] font-bold">ESGRYMA Consulting Group</span>, una consultora con un solo compromiso: cero accidentes.
        </p>
        <p className="text-gray-300 text-lg">
          La tecnología jugó un papel esencial en ese camino. Incorporamos{" "}
          <span className="text-[#FFD700] font-bold">Inteligencia Artificial en SST</span> 
          para predecir riesgos con una precisión cercana al 92%. 
          Lo que para muchos era ciencia ficción, para nosotros se convirtió en una herramienta diaria para salvar vidas. 
          En sectores de alto riesgo como{" "}
          <a href="/mineria" className="text-[#FFD700] font-bold hover:underline">minería</a> y{" "}
          <a href="/oil-gas" className="text-[#FFD700] font-bold hover:underline">oil & gas</a>, 
          logramos transformar plantas catalogadas como “zonas de peligro” en casos de éxito con años sin accidentes reportados.
        </p>

        {/* Capítulo 3 */}
        <h2 className="text-2xl font-bold mt-10 text-[#FFD700]">Capítulo 3: Códigos que Salvan Vidas</h2>
        <p className="text-gray-300 text-lg">
          En una minera peruana, instalamos sensores con algoritmos predictivos que detectaron vibraciones anómalas 72 horas antes de un derrumbe. 
          Gracias a esa alerta, evacuamos a tiempo y salvamos cuatro vidas. 
          No fue la suerte, fue la integración de{" "}
          <span className="text-[#FFD700] font-bold">IA en seguridad industrial</span>.  
          Este caso me marcó profundamente: la tecnología no reemplaza al ser humano, lo amplifica. 
          La clave está en formar líderes que sepan interpretar la información y tomar decisiones rápidas.
        </p>
        <p className="text-gray-300 text-lg">
          Por eso, además de la consultoría, promuevo programas de{" "}
          <a href="/formacion" className="text-[#FFD700] font-bold hover:underline">formación en SST 4.0</a>, 
          donde enseño cómo usar la IA no solo para prevenir accidentes, sino también para aumentar la productividad en las empresas. 
          Cuando un gerente entiende que reducir lesiones también significa menos costos y más eficiencia, la seguridad deja de ser vista como un gasto.
        </p>

        {/* Capítulo 4 */}
        <h2 className="text-2xl font-bold mt-10 text-[#FFD700]">Capítulo 4: El Precio de la Convicción</h2>
        <p className="text-gray-300 text-lg">
          En 2015 rechacé un contrato de medio millón de dólares con una empresa petrolera. 
          Habían ocultado fallas críticas en sus equipos y querían que firmara un informe validando la operación. 
          Mi respuesta fue un no rotundo. 
          Recordé la enseñanza de mi abuelo marinero: “El mar perdona errores, pero no perdona la avaricia”.  
          Ese día confirmé que la ética no se negocia.
        </p>
        <p className="text-gray-300 text-lg">
          Desde entonces, mi reputación se asocia a{" "}
          <span className="text-[#FFD700] font-bold">consultorías cero accidentes</span>, 
          respaldadas por estándares internacionales como{" "}
          <span className="text-[#FFD700] font-bold">NEBOSH</span>,{" "}
          <span className="text-[#FFD700] font-bold">OSHA</span> y normativas clave en Colombia como{" "}
          <a href="/retie" className="text-[#FFD700] font-bold hover:underline">RETIE</a> y{" "}
          <a href="/retilap" className="text-[#FFD700] font-bold hover:underline">RETILAP</a>. 
          Cada informe que entregamos no solo cumple con requisitos técnicos, sino que protege vidas y garantiza transparencia.
        </p>

        {/* Capítulo 5 */}
        <h2 className="text-2xl font-bold mt-10 text-[#FFD700]">Capítulo 5: El Futuro se Escribe sin Heridas</h2>
        <p className="text-gray-300 text-lg">
          Hoy lidero la visión de{" "}
          <span className="text-[#FFD700] font-bold">SST 4.0</span>, 
          un modelo que integra IoT, blockchain y analítica avanzada para anticipar fallas críticas en la industria. 
          Estamos desarrollando proyectos con{" "}
          <a href="/proyectos" className="text-[#FFD700] font-bold hover:underline">gemelos digitales de infraestructuras</a> 
          que permiten simular accidentes antes de que ocurran.  
          Imagine cascos que detectan fatiga antes de que el trabajador lo sienta o sensores que predicen explosiones por gases en tiempo real. 
          Ese futuro ya lo estamos construyendo hoy.
        </p>
        <p className="text-gray-300 text-lg">
          Mi objetivo es claro: que ningún trabajador pierda la vida por falta de prevención. 
          Y que cada empresa entienda que la seguridad no es un obstáculo, sino la base de la productividad sostenible. 
          Por eso invito a líderes, gerentes y trabajadores a unirse a esta revolución, 
          a través de mis conferencias, consultorías y del{" "}
          <a href="/blog" className="text-[#FFD700] font-bold hover:underline">blog especializado en IA aplicada a SST</a>.
        </p>

        {/* CTA Final */}
        <div className="mt-12">
          <button
            onClick={() => navigate("/blog")}
            className="bg-[#FFD700] text-black px-6 py-3 rounded-md font-semibold hover:bg-yellow-400 transition"
          >
            🚀 Visita el Blog y Únete a la Revolución en SST
          </button>
        </div>

      </div>
    </section>
  );
};

export default EmprendedorSection;
